import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Debounce } from "../../../../../services/Debounce";
import { RoleDetailsPageService } from "../service/RoleDetailsPageService";
import { useRolePermissionsContext } from "../context/RolePermissionsContext";
import { cloneNameToDisplay } from "../../../../Me-menu/utils/cloneNameUtils";

const ROLE_NAME_MAX_LENGTH = 50;
const ROLE_DESCRIPTION_MAX_LENGTH = 200;

type RoleInfoSectionProps = {
    roleNameError: string;
    clearRoleNameError: () => void;
};

const RoleInfoSection: React.FC<RoleInfoSectionProps> = ({ roleNameError, clearRoleNameError }) => {
    const { t: translate } = useTranslation();

    const { permissions, onChangeRoleName, onChangeRoleDescription, mode } = useRolePermissionsContext();
    const roleInfo = permissions.role_info;
    const [localRoleName, setLocalRoleName] = useState(cloneNameToDisplay(roleInfo.name, translate));
    const [localRoleDescription, setLocalRoleDescription] = useState(roleInfo.description || "");

    useEffect(() => {
        setLocalRoleName(cloneNameToDisplay(roleInfo.name, translate));
    }, [roleInfo.name, translate]);

    useEffect(() => {
        setLocalRoleDescription(roleInfo.description || "");
    }, [roleInfo.description]);

    const debouncedRoleNameChange = useMemo(
        () =>
            Debounce((value: string) => {
                onChangeRoleName(value);
            }, 300),
        [onChangeRoleName]
    );

    const onChangeRoleNameHandler = (value: string) => {
        clearRoleNameError();
        const sanitizedValue = RoleDetailsPageService.clearSpaces(value);
        setLocalRoleName(value);
        debouncedRoleNameChange(sanitizedValue);
    };

    const debouncedRoleDescriptionChange = useMemo(
        () => Debounce(onChangeRoleDescription, 300),
        [onChangeRoleDescription]
    );

    const onChangeRoleDescriptionHandler = (value: string) => {
        const sanitizedValue = RoleDetailsPageService.clearSpaces(value);

        setLocalRoleDescription(value);
        debouncedRoleDescriptionChange(sanitizedValue);
    };

    useEffect(() => {
        return () => {
            debouncedRoleNameChange.cancel?.();
            debouncedRoleDescriptionChange.cancel?.();
        };
    }, [debouncedRoleNameChange, debouncedRoleDescriptionChange]);

    const roleNameLength = localRoleName.length;
    const roleDescriptionLength = localRoleDescription.length;

    return (
        <div className="mb-5">
            <h4 className="fw-normal mb-4">{translate("Information")}</h4>

            <Row className={`mb-4 align-items-center ${mode === "view" ? "py-3" : ""}`}>
                <Col lg={2}>
                    {mode === "view" ? (
                        <p className={"mb-0 fw-bold"}>
                            <span className="text-nowrap fs-7">
                                {translate("Role Name")} <span className="text-danger">*</span>
                            </span>
                        </p>
                    ) : (
                        <label htmlFor="roleName" className="fw-bold pt-2">
                            <span className="text-nowrap fs-7">
                                {translate("Role Name")} <span className="text-danger">*</span>
                            </span>
                        </label>
                    )}
                </Col>

                <Col lg={10}>
                    {mode === "view" ? (
                        <p className={"mb-0 fs-7"}>{localRoleName}</p>
                    ) : (
                        <input
                            id="roleName"
                            type="text"
                            placeholder={translate("roles.addName")}
                            className="form-control fs-7"
                            value={localRoleName}
                            maxLength={ROLE_NAME_MAX_LENGTH}
                            onChange={(e) => onChangeRoleNameHandler(e.target.value)}
                        />
                    )}
                </Col>

                {roleNameError && (
                    <Col lg={{ span: 10, offset: 2 }}>
                        <div className="text-danger fs-7 mt-1">{translate(roleNameError)}</div>
                    </Col>
                )}

                {mode !== "view" && (
                    <Col lg={{ span: 10, offset: 2 }}>
                        <div className="text-grey fs-7 mt-1">
                            {roleNameLength}/{ROLE_NAME_MAX_LENGTH}
                        </div>
                    </Col>
                )}
            </Row>

            <Row>
                <Col lg={2}>
                    {mode === "view" ? (
                        <p className="mb-0 fw-bold pt-2 mt-1 fs-7">{translate("Role Description")}</p>
                    ) : (
                        <label htmlFor="roleDescription" className="fw-bold pt-2 mt-1 fs-7">
                            {translate("Role Description")}
                        </label>
                    )}
                </Col>

                <Col lg={10}>
                    {mode === "view" ? (
                        <p className={"f-7 mb-0"}>{localRoleDescription || "-"}</p>
                    ) : (
                        <textarea
                            id="roleDescription"
                            rows={4}
                            placeholder={translate("Enter a Description")}
                            value={localRoleDescription}
                            className="form-control fs-7"
                            style={{ resize: "none" }}
                            maxLength={ROLE_DESCRIPTION_MAX_LENGTH}
                            onChange={(e) => onChangeRoleDescriptionHandler(e.target.value)}
                        />
                    )}
                </Col>

                {mode !== "view" && (
                    <Col lg={{ span: 10, offset: 2 }}>
                        <div className="text-grey fs-7 mt-1">
                            {roleDescriptionLength}/{ROLE_DESCRIPTION_MAX_LENGTH}
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default React.memo(RoleInfoSection);
